import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Visibility,
} from '@mui/icons-material';
import { requestsAPI } from 'services/api';

const AgentRequestManagement = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [requests, setRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionType, setActionType] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const loadRequests = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await requestsAPI.getAllAdmin(statusFilter === 'all' ? null : statusFilter);
      setRequests(response.data);

    } catch (err) {
      console.error('Error loading requests:', err);
      setError('Failed to load requests. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const handleApproveRequest = async () => {
    if (!selectedRequest) return;

    try {
      await requestsAPI.approve(selectedRequest.id);
      await loadRequests();
      setActionDialogOpen(false);
      setSelectedRequest(null);
    } catch (err) {
      console.error('Error approving request:', err);
      setError('Failed to approve request. Please try again.');
    }
  };

  const handleRejectRequest = async () => {
    if (!selectedRequest || !rejectionReason.trim()) return;

    try {
      await requestsAPI.reject(selectedRequest.id, rejectionReason);
      await loadRequests();
      setActionDialogOpen(false);
      setSelectedRequest(null);
      setRejectionReason('');
    } catch (err) {
      console.error('Error rejecting request:', err);
      setError('Failed to reject request. Please try again.');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'success';
      case 'rejected': return 'error';
      case 'pending': return 'warning';
      default: return 'default';
    }
  };

  const getRequestTypeColor = (type) => {
    switch (type) {
      case 'seed': return 'primary';
      case 'feed': return 'secondary';
      case 'medicine': return 'info';
      case 'crop_sale': return 'success';
      case 'loan': return 'warning';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={loadRequests}>
            Retry
          </Button>
        }>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="subtitle1" color="textSecondary">
            Review and process farmer requests
          </Typography>
        </Box>
        
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Filter by Status</InputLabel>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            label="Filter by Status"
          >
            <MenuItem value="all">All Requests</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="approved">Approved</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Request ID</TableCell>
              <TableCell>Farmer</TableCell>
              <TableCell>Pond</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Quantity</TableCell>
              <TableCell>Cost</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Requested Date</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {requests.map((request) => (
              <TableRow key={request.id}>
                <TableCell>{request.request_number}</TableCell>
                <TableCell>{request.user_name}</TableCell>
                <TableCell>{request.pond_name}</TableCell>
                <TableCell>
                  <Chip 
                    label={request.request_type} 
                    color={getRequestTypeColor(request.request_type)}
                    size="small"
                  />
                </TableCell>
                <TableCell>{request.title}</TableCell>
                <TableCell>
                  {request.quantity} {request.unit}
                </TableCell>
                <TableCell>
                  ₹{request.estimated_cost ? parseFloat(request.estimated_cost).toFixed(2) : 'N/A'}
                </TableCell>
                <TableCell>
                  <Chip 
                    label={request.status} 
                    color={getStatusColor(request.status)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {new Date(request.requested_date).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  <Box display="flex" gap={1}>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSelectedRequest(request);
                        setActionType('view');
                        setActionDialogOpen(true);
                      }}
                    >
                      <Visibility />
                    </IconButton>
                    
                    {request.status === 'pending' && (
                      <>
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() => {
                            setSelectedRequest(request);
                            setActionType('approve');
                            setActionDialogOpen(true);
                          }}
                        >
                          <CheckCircle />
                        </IconButton>
                        
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            setSelectedRequest(request);
                            setActionType('reject');
                            setActionDialogOpen(true);
                          }}
                        >
                          <Cancel />
                        </IconButton>
                      </>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {requests.length === 0 && (
        <Box textAlign="center" py={4}>
          <Typography variant="h6" color="textSecondary">
            No requests found
          </Typography>
        </Box>
      )}

      {/* Action Dialog */}
      <Dialog open={actionDialogOpen} onClose={() => setActionDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {actionType === 'view' ? 'View Request Details' : 
           actionType === 'approve' ? 'Approve Request' : 'Reject Request'}
        </DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Box>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Request ID</Typography>
                  <Typography variant="body1">{selectedRequest.request_number}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Farmer</Typography>
                  <Typography variant="body1">{selectedRequest.user_name}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Pond</Typography>
                  <Typography variant="body1">{selectedRequest.pond_name}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Type</Typography>
                  <Chip 
                    label={selectedRequest.request_type} 
                    color={getRequestTypeColor(selectedRequest.request_type)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">Title</Typography>
                  <Typography variant="body1">{selectedRequest.title}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">Description</Typography>
                  <Typography variant="body1">{selectedRequest.description || 'No description provided'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Quantity</Typography>
                  <Typography variant="body1">{selectedRequest.quantity} {selectedRequest.unit}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Estimated Cost</Typography>
                  <Typography variant="body1">₹{selectedRequest.estimated_cost ? parseFloat(selectedRequest.estimated_cost).toFixed(2) : 'N/A'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Requested Date</Typography>
                  <Typography variant="body1">
                    {new Date(selectedRequest.requested_date).toLocaleDateString()}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Status</Typography>
                  <Chip 
                    label={selectedRequest.status} 
                    color={getStatusColor(selectedRequest.status)}
                    size="small"
                  />
                </Grid>
              </Grid>

              {actionType === 'reject' && (
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Rejection Reason"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  margin="normal"
                  placeholder="Please provide a reason for rejection..."
                  required
                />
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionDialogOpen(false)}>Cancel</Button>
          {actionType === 'approve' && (
            <Button onClick={handleApproveRequest} variant="contained" color="success">
              Approve Request
            </Button>
          )}
          {actionType === 'reject' && (
            <Button 
              onClick={handleRejectRequest} 
              variant="contained" 
              color="error"
              disabled={!rejectionReason.trim()}
            >
              Reject Request
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AgentRequestManagement;
